import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Environment, Filter } from '@playq/octopus-common';
import {
  ClusterizationTaskBrief,
  ClusterizationTaskCreateData,
  ClusterizationTaskId,
  ClusterizationTaskSort,
  ClusterizationTasksResponse,
} from '@playq/services-beetle';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { services2 } from '/api';
import { IUseQueryOptionsExtended, useEitherQuery, useMutateQueryDataAfterEntityDeleting } from '/api/service-hooks';

const taskKeys: unknown[] = ['tasks', 'query'];
export const useQueryTasks = (
  env: Environment,
  iterator: OffsetLimit,
  sortBy: ClusterizationTaskSort[],
  filterBy: {
    [key: string]: Filter;
  },
  options?: IUseQueryOptionsExtended<ClusterizationTasksResponse | undefined, GenericFailure | Error>
) => {
  const { mutate, refetch, ...res } = useEitherQuery(
    taskKeys.concat(env, sortBy, iterator, filterBy),
    () => {
      return services2.audienceRequestsClusterizationService.queryTasks(iterator, sortBy, filterBy, env);
    },
    {
      placeholderData: keepPreviousData,
      ...options,
    }
  );
  const queryClient = useQueryClient();
  const createTaskCache = ({ id, task }: { id: ClusterizationTaskId; task: ClusterizationTaskCreateData }) => {
    mutate((prev) => {
      if (!prev) {
        return;
      }

      const newResponse = new ClusterizationTasksResponse(prev.serialize());
      const clusterTaskBriefData = task.toClusterizationTaskBriefDataSerialized();
      const clusterTaskBrief = new ClusterizationTaskBrief({
        ...clusterTaskBriefData,
        id: id.serialize(),
        latestRun: undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      newResponse.requests = [clusterTaskBrief, ...prev.requests];
      newResponse.total = prev.total + 1;

      return newResponse;
    });
  };

  const tasks = useMemo(() => res.data?.requests || [], [res.data?.requests]);
  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);
  const mutateDeletedIssue = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    // nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (task?: ClusterizationTaskBrief) => task?.id,
    getEntities: (task: ClusterizationTasksResponse) => task.requests,
    setEntities: (task: ClusterizationTasksResponse, newEntities: ClusterizationTaskBrief[]) => {
      task.requests = newEntities;
    },
    createNewResponse: (prevResponse: ClusterizationTasksResponse) =>
      new ClusterizationTasksResponse(prevResponse.serialize()),
  });
  return { ...res, tasks, total, createTaskCache, mutateDeletedIssue, refetch };
};
